import { convertForLocale } from 'util/unitConversion';
import { CalculatedRow, ParseRecommendationRowParams } from './types';
import { Recommendation, FieldData } from 'create/bulk/common/types';
import { DropdownState } from './CurveModal/types';

interface CalculateEstimatedYieldParams {
  seedingRate: number;
  curve: Recommendation['curve'] | undefined;
  cropNominalWeight: number;
}

export const calculateEstimatedYield = ({
  seedingRate,
  curve,
  cropNominalWeight
}: CalculateEstimatedYieldParams): number => {
  if (!curve?.plantingRate || !curve.yield || curve.plantingRate.length < 2) {
    return 0;
  }

  const { plantingRate, yield: yieldValues, plantingRateUnit } = curve;

  const convertedPlantingRates = plantingRate.map((rate) =>
    convertForLocale({
      quantity: rate,
      unit: plantingRateUnit,
      unitType: 'seed-density',
      cropNominalWeight
    }).scalar
  );

  let lowerIndex = 0;
  let upperIndex = convertedPlantingRates.length - 1;

  for (let i = 0; i < convertedPlantingRates.length - 1; i++) {
    if (convertedPlantingRates[i] <= seedingRate && convertedPlantingRates[i + 1] >= seedingRate) {
      lowerIndex = i;
      upperIndex = i + 1;
      break;
    }
  }

  const convertedYields = yieldValues.map((y) =>
    convertForLocale({
      quantity: y,
      unit: curve.yieldUnit,
      unitType: 'area-density.yield',
      cropNominalWeight
    }).scalar
  );

  if (seedingRate <= convertedPlantingRates[0]) {
    return convertedYields[0];
  }
  if (seedingRate >= convertedPlantingRates[convertedPlantingRates.length - 1]) {
    return convertedYields[convertedPlantingRates.length - 1];
  }

  const seedingRate0 = convertedPlantingRates[lowerIndex];
  const seedingRate1 = convertedPlantingRates[upperIndex];
  const yield0 = convertedYields[lowerIndex];
  const yield1 = convertedYields[upperIndex];

  const estimatedYield = yield0 + (
    seedingRate - seedingRate0
  ) * (
    (yield1 - yield0) / (seedingRate1 - seedingRate0)
  );

  return estimatedYield;
};

interface CalculateSeedsCostAndBagsNeededParams {
  totalArea: number;
  avgSeedingRate: number;
  bagSize: number;
  seedCostPerBag: number;
}
/**
 * Calculates the number of seed bags needed, and the seed cost per area.
 * Total area and seeding rate must be in compatible units (eg. acres, and seeds per acre).
*/
export const calculateSeedCostAndBagsNeeded = (
  { totalArea, avgSeedingRate, bagSize, seedCostPerBag }: CalculateSeedsCostAndBagsNeededParams
) => {
  const bagsNeeded = totalArea * avgSeedingRate / bagSize;
  const seedCostPerArea = (bagsNeeded * seedCostPerBag) / totalArea;

  return {
    bagsNeeded,
    seedCostPerArea
  };
};

interface CalculateProjectedGrossRevenueParams {
  estimatedYield: number;
  grainPrice: number;
  seedCostPerArea: number;
}

/**
 * Calculates the projected gross revenue per area, given an estimated yield, grain price and seed cost per area.
 * The estimated yield must be in compatible units with the seed cost per area
 * (eg. bushels per acre, and any currency per acre).
 */
export const calculateProjectedRevenuePerArea = (
  { estimatedYield, grainPrice, seedCostPerArea }: CalculateProjectedGrossRevenueParams
) => {
  const avgEstGrossRevenue = estimatedYield * grainPrice;
  const projectedRevenuePerArea = avgEstGrossRevenue - seedCostPerArea;
  
  return projectedRevenuePerArea;
};

export const parseRecommendationRow = ({
  id,
  seedingRate,
  curve,
  convertedFieldTotalArea,
  bagSize,
  seedCostPerBag,
  grainPrice,
  cropNominalWeight,
  isPreferredPlan
}: ParseRecommendationRowParams): CalculatedRow | undefined => {
  if (!seedingRate) return;

  const avgFixedPop = convertForLocale({
    quantity: seedingRate.v || 0,
    unit: seedingRate.u,
    unitType: 'seed-density',
    cropNominalWeight
  });

  const { bagsNeeded, seedCostPerArea } = calculateSeedCostAndBagsNeeded({
    totalArea: convertedFieldTotalArea,
    avgSeedingRate: avgFixedPop.scalar,
    bagSize,
    seedCostPerBag
  });
  const estimatedYield = calculateEstimatedYield({ seedingRate: avgFixedPop.scalar, curve, cropNominalWeight });
  const projectedRevenuePerArea = calculateProjectedRevenuePerArea({ estimatedYield, grainPrice, seedCostPerArea });

  return {
    id,
    avgFixedPop: avgFixedPop.scalar,
    estimatedYield,
    bagsNeeded,
    seedCostPerArea,
    projectedRevenuePerArea,
    isPreferredPlan
  };
};

/**
 * Gets the recommendation by using the hybrid id,
 * since it will be the unique value that identifies it among
 * all the possible recommendations one field can have.
 */

export const getRecommendationByHybridId = ({
  fields,
  fieldId,
  hybridId
}: {
  fields: FieldData[];
  fieldId: FieldData['id'];
  hybridId: DropdownState['id'] | null;
}): FieldData | undefined => {
  return fields.filter((f) => f.id === fieldId).find(
    (f) => f.hybrid.id === hybridId,
  );
};

/**
 * Finds the index of the field in the fields array object.
 */

export const findFieldIndexByHybridId = ({
  fields,
  fieldId,
  hybridId
}: {
  fields: FieldData[];
  fieldId: FieldData['id'];
  hybridId: DropdownState['id'] | null;
}): number => {
  return fields.findIndex(
    (f) => f.hybrid.id === hybridId && f.id === fieldId
  );
};

/**
 * Determines the dataset to be plotted in the chart by using the hybrid 
 * as key to determine it.
 */

export const getSelectedDataSet = ({
  hybrid,
  fields,
  fieldId,
  cropNominalWeight
}: {
  hybrid: DropdownState | null;
  fields: FieldData[];
  fieldId: FieldData['id'];
  cropNominalWeight: number;
}) => {
  if (!hybrid?.id) return null;
  const selectedRecommendation = getRecommendationByHybridId({ fields, fieldId, hybridId: hybrid.id });

  if (!selectedRecommendation) return null;
  return selectedRecommendation.recommendation.curve.plantingRate.map(
    (rate, index) => ({
      plantingRate: convertForLocale({
        quantity: rate,
        unit: selectedRecommendation.recommendation.curve.plantingRateUnit,
        unitType: 'seed-density',
        cropNominalWeight,
      }).scalar,
      yield: convertForLocale({
        quantity: selectedRecommendation.recommendation.curve.yield[index],
        unit: selectedRecommendation.recommendation.curve.yieldUnit,
        unitType: 'area-density.yield',
        cropNominalWeight,
      }).scalar,
    }),
  );
};